import { Divider, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyHeader from '../../components/StickyHeader';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import ChatMessage from '../../components/chats/ChatMessage';
import { Xmark } from '@peacecover/icon';
import { t } from 'i18next';

const QUESTIONS = {
  'S-N': [
    {
      content: t('FanMBTIChatBot.question.s-n'),
      messageType: 'text',
      isMine: false
    }
  ],
  'J-P': [
    {
      content: t('FanMBTIChatBot.question.j-p'),
      messageType: 'text',
      isMine: false
    }
  ],
  'F-T': [
    { content: t('FanMBTIChatBot.question.f-t_1'), messageType: 'text', isMine: false },
    {
      content: t('FanMBTIChatBot.question.f-t_2'),
      messageType: 'text',
      isMine: false
    }
  ],
  'E-I': [
    {
      content: t('FanMBTIChatBot.question.e-i'),
      messageType: 'text',
      isMine: false
    }
  ],
  FINISH: []
};

const RESPONSES: {
  [key: string]: {
    [key: string]: {
      nextQuestion: string;
      responseType: string;
    };
  };
} = {
  'S-N': {
    [t('FanMBTIChatBot.response.type_s')]: {
      nextQuestion: 'J-P',
      responseType: 'S'
    },
    [t('FanMBTIChatBot.response.type_n')]: {
      nextQuestion: 'J-P',
      responseType: 'N'
    }
  },
  'J-P': {
    [t('FanMBTIChatBot.response.type_p')]: {
      nextQuestion: 'F-T',
      responseType: 'P'
    },
    [t('FanMBTIChatBot.response.type_j')]: {
      nextQuestion: 'F-T',
      responseType: 'J'
    }
  },
  'F-T': {
    [t('FanMBTIChatBot.response.type_f')]: {
      nextQuestion: 'E-I',
      responseType: 'F'
    },
    [t('FanMBTIChatBot.response.type_t')]: {
      nextQuestion: 'E-I',
      responseType: 'T'
    }
  },
  'E-I': {
    [t('FanMBTIChatBot.response.type_e')]: {
      nextQuestion: 'FINISH',
      responseType: 'E'
    },
    [t('FanMBTIChatBot.response.type_i')]: {
      nextQuestion: 'FINISH',
      responseType: 'I'
    }
  },
  FINISH: {
    [t('FanMBTIChatBot.nextButton')]: {
      nextQuestion: 'NEXT_PAGE',
      responseType: 'navigation'
    }
  }
};

export default function FanMBTIChatBotPage() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState('S-N');
  // const [chatHistory, setChatHistory] = useState([]);
  // const [showResponses, setShowResponses] = useState(false);

  // 초기 질문을 대화 기록에 추가
  const initialQuestions = QUESTIONS[currentQuestionIndex as keyof typeof QUESTIONS];
  const initialChatHistory = [...initialQuestions]; // 첫 번째 질문을 추가

  const chatContainerRef = useRef<HTMLDivElement>(null); // 스크롤을 위한 ref 추가

  // 상태 초기화
  const [chatHistory, setChatHistory] = useState(initialChatHistory);
  const [showResponses, setShowResponses] = useState(true);
  const [responseTypes, setResponseTypes] = useState<any[]>([]); // 선택된 responseType을 저장할 상태 추가

  const navigate = useNavigate();

  const exit = () => {
    navigate('/home', { replace: true });
  };

  // 응답 버튼 클릭 시 다음 질문으로 이동
  const handleButtonClick = (response: {
    content: string;
    messageType: string;
    isMine: boolean;
  }) => {
    // 다음 질문으로 이동
    const nextQuestionIndex = RESPONSES[currentQuestionIndex][response.content].nextQuestion;
    if (nextQuestionIndex === 'NEXT_PAGE') {
      return navigate(`/fan-buddy/personal-keywords?mbti=${sortedResponseTypes}`);
    }
    setShowResponses(false);

    const newMessage = {
      content: response.content,
      messageType: response.messageType,
      isMine: true,
      sentAt: Date.now()
    };

    // 대화 기록에 현재 응답 추가
    setChatHistory((prev) => {
      const updatedChatHistory = [...prev, newMessage];
      // 스크롤을 아래로 내리기
      if (chatContainerRef.current) {
        setTimeout(() => {
          chatContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          });
        }, 500);
      }
      return updatedChatHistory;
    });

    // 다음 질문을 표시하기 위해 currentQuestionIndex 업데이트
    setCurrentQuestionIndex(nextQuestionIndex);

    // 다음 질문을 대화 기록에 추가
    const nextQuestions = QUESTIONS[nextQuestionIndex as keyof typeof QUESTIONS];

    if (isEmpty(nextQuestions)) {
      setTimeout(() => {
        setShowResponses(true);
      }, 500);
    } else {
      nextQuestions.forEach((question, index) => {
        setTimeout(
          () => {
            setChatHistory((prev) => [...prev, question]);
            if (index + 1 === nextQuestions.length) {
              setShowResponses(true);
            }
          },
          (index + 1) * 1000
        ); // 첫 index부터 1초 간격으로 질문 추가
      });
    }

    // 현재 응답 값의 타입을 확인할 수 있습니다.
    const responseType = RESPONSES[currentQuestionIndex][response.content].responseType;
    setResponseTypes((prev: any) => [...prev, responseType]); // 선택된 responseType 저장
  };

  // MBTI 순으로 정렬된 responseTypes를 반환하는 함수
  const sortedResponseTypes = useMemo(() => {
    const mbtiOrder = ['E', 'I', 'S', 'N', 'T', 'F', 'J', 'P'];
    return responseTypes.sort((a, b) => mbtiOrder.indexOf(a) - mbtiOrder.indexOf(b)).join(''); // 정렬된 후 JOIN 한 스트링으로 변환
  }, [responseTypes]);

  return (
    <>
      <StickyHeader
        title={t('FanMBTIChatBot.pageTitle') as string}
        alwaysShowTitle={true}
        showBackButton={false}
        leftContent={<Xmark size={24} className="text-text-main-lv1" onClick={exit} />}
        showTitle={true}
      />
      <div className="pt-[calc(64px+env(safe-area-inset-top))] px-[24px]">
        <VStack style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div
            ref={chatContainerRef}
            style={{
              width: '100%',
              flex: 1,
              overflowY: 'auto',
              paddingBottom: currentQuestionIndex === 'FINISH' ? '240px' : '320px'
            }}
          >
            <motion.div>
              {chatHistory.map((msg, index) => (
                <motion.div
                  // biome-ignore lint/suspicious/noArrayIndexKey: this is a key for the message
                  key={index}
                  initial={{ opacity: 0, y: -10 }} // 위에서 아래로 이동
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ marginBottom: '10px' }} // 각 메시지 간의 간격 추가
                >
                  <ChatMessage message={msg as any} isLastUnSeenMessage={false} chatRoom={null} />
                </motion.div>
              ))}
            </motion.div>
          </div>
          {showResponses && (
            <VStack
              className="!bg-background-light dark:!bg-background-dark"
              spacing={2}
              style={{
                position: 'fixed',
                bottom: 'calc(4px + env(safe-area-inset-bottom))',
                width: '100%',
                padding: '20px 0'
              }}
            >
              <Divider />
              {currentQuestionIndex !== 'FINISH' && (
                <motion.div
                  initial={{ opacity: 0, y: 0 }} // 아래에서 위로 나타남
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }} // 딜레이 추가
                >
                  <p className="pt-[14px] pb-[10px] text-[14px] font-medium leading-[17px] text-center text-text-main-lv4">
                    {t('FanMBTIChatBot.clickCloseAnswerDescription')}
                  </p>
                </motion.div>
              )}

              {Object.entries(RESPONSES[currentQuestionIndex]).map(([responseContent], _index) => (
                <motion.div
                  key={responseContent}
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.1, delay: 0.5 }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <RegularSolidButton
                    text={responseContent}
                    onClicked={() =>
                      handleButtonClick({
                        content: responseContent,
                        messageType: 'text',
                        isMine: true
                      })
                    }
                    style={{
                      fontSize: '15px',
                      fontWeight: 500,
                      padding: '16px 20px',
                      width: 'calc(100% - 48px)',
                      borderRadius: '8px'
                    }}
                  />
                </motion.div>
              ))}
            </VStack>
          )}
        </VStack>
      </div>
    </>
  );
}
